// Formato de montos de dinero — convención única del POS (RF-7 del SPEC
// `ux-operativa-v1-spec.md`).
//
// El separador es EXCLUSIVAMENTE visual: `1.250.000` en pantalla, `1250000` en
// la request. Ningún payload cambia por usar esto.
//
// En `es-PY` el separador de miles es el punto, que es lo que el usuario final
// llama "punto decimal" y la forma en que se escriben los guaraníes.
//
// Decimales: todos los campos del sistema son enteros. Toda la UI ya redondea
// al mostrar (`Math.round` / `maximumFractionDigits: 0`) y el precio de granel
// se persiste por kilogramo, sin fracción. El parámetro existe con default 0
// para que un campo futuro que sí los necesite no obligue a reescribir esto.

const LOCALE = 'es-PY';

// Devuelve sólo los dígitos de un texto. Sirve para pegar `₲ 1.250.000` o
// `1,250,000` y quedarse con `1250000`.
export function onlyDigits(value) {
  return String(value ?? '').replace(/\D/g, '');
}

// Número -> texto formateado. `null`/`undefined`/`''` dan `''`, no `'0'`:
// un campo vacío tiene que verse vacío, no cargado en cero.
export function formatMoney(value, decimals = 0) {
  if (value === null || value === undefined || value === '') return '';
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return number.toLocaleString(LOCALE, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
}

// Texto formateado -> número. Devuelve `null` para vacío, nunca `NaN`: quien
// consume esto distingue "no cargó nada" de "cargó cero".
export function parseMoney(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return Number.isFinite(value) ? Math.trunc(value) : null;
  const digits = onlyDigits(value);
  if (!digits) return null;
  const number = Number(digits);
  return Number.isFinite(number) ? number : null;
}

// Reformatea preservando la posición del cursor. Sin esto el cursor salta al
// final en cada tecla y editar en el medio de un monto se vuelve imposible.
//
// Se cuenta cuántos dígitos había antes del cursor y se lo reubica después del
// mismo dígito una vez reinsertados los separadores: contar caracteres no
// sirve porque la cantidad de puntos cambia.
export function applyMoneyMask(rawValue, selectionStart, decimals = 0) {
  const digitsBefore = onlyDigits(String(rawValue ?? '').slice(0, selectionStart ?? 0)).length;
  const numeric = parseMoney(rawValue);
  const formatted = numeric === null ? '' : formatMoney(numeric, decimals);

  let seen = 0;
  let cursor = formatted.length;
  for (let i = 0; i < formatted.length; i += 1) {
    if (/\d/.test(formatted[i])) {
      seen += 1;
      if (seen === digitsBefore) {
        cursor = i + 1;
        break;
      }
    }
  }
  if (digitsBefore === 0) cursor = 0;

  return { formatted, numeric, cursor };
}
